import { useCallback, useEffect, useMemo, useState } from 'react';

import { graphql, useFragment } from 'react-relay';

import { DEFAULT_COLLAPSED_KEY, DEFAULT_WIDTH_KEY } from '../../../../common/constants';
import type {
	NetworkStorageParams,
	Store,
	UserMenuData,
	UserSideNavData,
} from '../../../../common/types';
import {
	buildNavigationAriForMenuData,
	buildNavigationAriForSidebarData,
} from '../../../../common/utils/navigation-ari';
import { useNavSettingsService } from '../use-nav-settings-service';

import { type useNetworkSideNavUserData$key } from './__generated__/useNetworkSideNavUserData.graphql';

// Pure function to handle user side nav data updates - moved outside component for stability
const setUserSideNavDataFixed = (
	userSideNavData: UserSideNavData,
	currentDefaultWidth: string,
	currentDefaultCollapsed: string,
	setSideNavPropertiesCallback: (properties: {
		defaultCollapsed: string;
		defaultWidth: string;
	}) => void,
	setSidebarNavDataCallback: (data: { properties: Array<{ key: string; value: string }> }) => void,
) => {
	const newState = {
		defaultWidth: String(userSideNavData.defaultWidth ?? currentDefaultWidth) || '',
		defaultCollapsed:
			String(userSideNavData.defaultCollapsed ?? currentDefaultCollapsed) || 'false',
	};
	setSideNavPropertiesCallback(newState);
	const properties = [
		{ key: DEFAULT_WIDTH_KEY, value: newState.defaultWidth },
		{ key: DEFAULT_COLLAPSED_KEY, value: newState.defaultCollapsed },
	];
	setSidebarNavDataCallback({ properties });
};

type Params = NetworkStorageParams & {
	firstPartyApp: string;
};

/**
 * useNetworkSideNavUserData - returns a tuple of user-data and a setter function to update the user customization data.
 * This can be used by the CustomizationModal to update the sidebar customization data.
 */
export function useNetworkSideNavUserData(params: Params): Store {
	const { firstPartyApp, activationId = '', ownerAri, queryRef, siteId = '' } = params;

	const { queryData: menuQueryData, setNavData: setMenuNavData } = useNavSettingsService({
		mutationEntityAri: buildNavigationAriForMenuData({ activationId, firstPartyApp, siteId }),
		ownerAri,
		queryRef: queryRef.navPlatL1MenuData,
	});

	const { queryData: sidebarQueryData, setNavData: setSidebarNavData } = useNavSettingsService({
		mutationEntityAri: buildNavigationAriForSidebarData({ activationId, firstPartyApp, siteId }),
		ownerAri,
		queryRef: queryRef.navPlatSidebarData,
	});

	// We store the state of the properties & customizationItems, as the sidebar state can change without any
	// new calls to the local queryRef (to read the value back). So we persist it in this component.
	const [sideNavProperties, setSideNavProperties] = useState({
		defaultWidth:
			(sidebarQueryData?.properties?.nodes ?? [{ key: DEFAULT_WIDTH_KEY, value: undefined }]).find(
				(item) => item?.key === DEFAULT_WIDTH_KEY,
			)?.value ?? '',
		defaultCollapsed:
			(
				sidebarQueryData?.properties?.nodes ?? [{ key: DEFAULT_COLLAPSED_KEY, value: undefined }]
			).find((item) => item?.key === DEFAULT_COLLAPSED_KEY)?.value ?? '',
	});
	const [customizationItems, setCustomizationItems] = useState(
		// Business rule:  The user-customization-data (menu data):
		// 	- uses the new-record nodes (if they exist), or...
		//  - is an empty array (no user customization).
		// This means that if the settings service fails, we return empty user-customization data,
		// which causes GSN to render everything the *app* wants in the app-default order.
		repairCustomizationItems(menuQueryData?.sidebar?.nodes || []),
	);

	// Create two callback functions - one for each user data-prop.
	// Not sure how performant this is, but it simplifies updates (because the caller doesn't need to know about the other half of the data)
	const setUserMenuData = useCallback(
		(rawSidebarData: UserMenuData) => {
			setCustomizationItems(rawSidebarData);

			setMenuNavData({ sidebar: sanitizeUserMenuData(rawSidebarData) });
		},
		[setMenuNavData],
	);

	// A non-memoized version was created as part of https://hello.jira.atlassian.cloud/browse/NAVX-2972
	// Memoize to keep a stable setter reference when state is unchanged.
	const setUserSideNavData = useCallback(
		(userSideNavData: UserSideNavData) =>
			setUserSideNavDataFixed(
				userSideNavData,
				sideNavProperties.defaultWidth,
				sideNavProperties.defaultCollapsed,
				setSideNavProperties,
				setSidebarNavData,
			),
		[
			sideNavProperties.defaultWidth,
			sideNavProperties.defaultCollapsed,
			setSideNavProperties,
			setSidebarNavData,
		],
	);

	// Create a nicer shape for the data.
	// For the userSideNavData, it is ok for the values to be undefined,
	// as the ADS component will know what to do.

	const stableUserData = useMemo(
		() => ({
			userMenuData: customizationItems,
			userSideNavData: toUserSideNavData(sideNavProperties),
			setUserMenuData,
			setUserSideNavData,
		}),
		[customizationItems, sideNavProperties, setUserMenuData, setUserSideNavData],
	);

	return stableUserData;
}

/**
 * useNetworkMigrationSideNavUserData - the data-migration version of useNetworkSideNavUserData()
 */
export function useNetworkMigrationSideNavUserData(params: Params): Store {
	const { firstPartyApp, activationId = '', ownerAri, queryRef, siteId = '' } = params;
	const queryDataOLD = useLocalFragmentToGetOldEntityAri(queryRef); // Read from the existing entityAri record

	const { queryData: l1menuQueryData, setNavData: setL1MenuData } = useNavSettingsService({
		mutationEntityAri: buildNavigationAriForMenuData({ activationId, firstPartyApp, siteId }),
		ownerAri,
		queryRef: queryRef.navPlatL1MenuData,
	});

	const { queryData: sidebarSizeQueryData, setNavData: setSidebarSizeData } = useNavSettingsService(
		{
			mutationEntityAri: buildNavigationAriForSidebarData({ activationId, firstPartyApp, siteId }),
			ownerAri,
			queryRef: queryRef.navPlatSidebarData,
		},
	);

	// We store the state of the properties & customizationItems, as the sidebar state can change without any
	// new calls to the local queryRef (to read the value back). So we persist it in this component.
	const [sideNavProperties, setSideNavProperties] = useState({
		defaultWidth:
			(
				(sidebarSizeQueryData?.properties?.nodes ||
					queryDataOLD?.userSideNavSettings?.properties?.nodes) ?? [
					{ key: DEFAULT_WIDTH_KEY, value: undefined },
				]
			).find((item) => item?.key === DEFAULT_WIDTH_KEY)?.value ?? '',
		defaultCollapsed:
			(
				(sidebarSizeQueryData?.properties?.nodes ||
					queryDataOLD?.userSideNavSettings?.properties?.nodes) ?? [
					{ key: DEFAULT_COLLAPSED_KEY, value: undefined },
				]
			).find((item) => item?.key === DEFAULT_COLLAPSED_KEY)?.value ?? '',
	});
	const [sideNavMenuData, setSideNavMenuData] = useState(
		repairCustomizationItems(
			// Business rule:  The user-customization-data (menu data):
			// 	- uses the new-record nodes (if they exist), or...
			//	- uses the old-record nodes (if they exist), or...
			//  - is an empty array (no user customization).
			// This means that if the settings service fails, we return empty user-customization data,
			// which causes GSN to render everything the *app* wants in the app-default order.
			l1menuQueryData?.sidebar?.nodes || queryDataOLD?.userSideNavSettings?.sidebar?.nodes || [],
		),
	);

	// Create two callback functions - one for each user data-prop.
	const setUserMenuData = useCallback(
		(rawSidebarData: UserMenuData) => {
			setSideNavMenuData(rawSidebarData);

			setL1MenuData({ properties: [], sidebar: sanitizeUserMenuData(rawSidebarData) });
		},
		[setL1MenuData],
	);

	// A non-memoized version was created as part of https://hello.jira.atlassian.cloud/browse/NAVX-2972
	// Memoize to keep a stable setter reference when state is unchanged.
	const setUserSideNavData = useCallback(
		(userSideNavData: UserSideNavData) => {
			const newState = {
				defaultWidth: String(userSideNavData.defaultWidth ?? sideNavProperties.defaultWidth) || '',
				defaultCollapsed:
					String(userSideNavData.defaultCollapsed ?? sideNavProperties.defaultCollapsed) || 'false',
			};
			setSideNavProperties(newState);
			const properties = [
				{ key: DEFAULT_WIDTH_KEY, value: newState.defaultWidth },
				{ key: DEFAULT_COLLAPSED_KEY, value: newState.defaultCollapsed },
			];
			setSidebarSizeData({ properties, sidebar: [] });
		},
		[
			sideNavProperties.defaultWidth,
			sideNavProperties.defaultCollapsed,
			setSideNavProperties,
			setSidebarSizeData,
		],
	);

	// Migration behaviour runs ONCE after the browser (we don't want to do this on the server) becomes idle for 2 seconds.
	// If there is data in the old record but no data in the new record, fix this up after idle
	useEffect(() => {
		const handleMigration = () => {
			// MENU data migration
			const oldMenuData = sideNavMenuData;
			const newMenuData = l1menuQueryData?.sidebar?.nodes || [];

			if (oldMenuData.length > 0 && newMenuData.length === 0) {
				setUserMenuData(oldMenuData);
			}

			// SIDEBAR data migration
			const oldSidebarData = queryDataOLD?.userSideNavSettings?.properties?.nodes || [];
			const newSidebarData = sidebarSizeQueryData?.properties?.nodes || [];

			if (oldSidebarData.length > 0 && newSidebarData.length === 0) {
				setUserSideNavData(toUserSideNavData(sideNavProperties));
			}
		};

		const searchParams = new URLSearchParams(location.search);

		// Add a way to reset the data in the NEW records, to allow us to re-test the migration process on an ad-hoc basis
		if (searchParams.has('reset-migration')) {
			// Bypass the data formatters and reset the data. These values will be treated as 'empty' by the code.
			return callWhenIdle(() => {
				setL1MenuData({ properties: [], sidebar: [] });
				setSidebarSizeData({ properties: [], sidebar: [] });
			});
		} else {
			return callWhenIdle(handleMigration);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Create a nicer shape for the data.
	// For the userSideNavData, it is ok for the values to be undefined,
	// as the ADS component will know what to do.

	const stableUserData = useMemo(
		() => ({
			userMenuData: sideNavMenuData,
			userSideNavData: toUserSideNavData(sideNavProperties),
			setUserMenuData,
			setUserSideNavData,
		}),
		[sideNavMenuData, sideNavProperties, setUserMenuData, setUserSideNavData],
	);

	return stableUserData;
}

/**
 * Make the Relay data look "normal" to the outside world.
 */
function repairCustomizationItems(value: any): UserMenuData {
	return value satisfies UserMenuData;
}

function toUserSideNavData(sideNavProperties: Record<string, string>) {
	return {
		defaultWidth:
			sideNavProperties.defaultWidth === '' ? undefined : Number(sideNavProperties.defaultWidth),
		defaultCollapsed:
			sideNavProperties.defaultCollapsed === ''
				? undefined
				: sideNavProperties.defaultCollapsed === 'true',
	};
}

function useLocalFragmentToGetOldEntityAri(queryRef: NetworkStorageParams['queryRef']) {
	/* eslint-disable @atlassian/relay/no-wrapping-hooks */
	/* eslint-disable @atlassian/relay/unused-fields */
	/* eslint-disable @atlassian/relay/graphql-naming */
	return useFragment<useNetworkSideNavUserData$key>(
		graphql`
			fragment useNetworkSideNavUserData on Query {
				userSideNavSettings: settings_navigationCustomisation(
					entityAri: $entityAriUserSideNavSettings
					ownerAri: $ownerAri
				) {
					sidebar {
						nodes {
							menuId
							visible
						}
					}
					properties {
						nodes {
							key
							value
						}
					}
				}
			}
		`,
		queryRef,
	);
	/* eslint-enable @atlassian/relay/unused-fields */
}

/**
 * Creates a new UserMenuData (array) that is supported by the Settings Service.
 */
function sanitizeUserMenuData(data: UserMenuData): UserMenuData {
	return data.map((item) => ({ menuId: item.menuId, visible: item.visible }));
}

function callWhenIdle(callback: () => void): () => void {
	if (typeof requestIdleCallback !== 'undefined') {
		const id = requestIdleCallback(callback);
		return () => cancelIdleCallback(id);
	}

	const id = requestAnimationFrame(callback);
	return () => cancelAnimationFrame(id);
}
